// services/dataService.ts
import axios, { AxiosInstance } from "axios";
import { QueryFilters, PaginatedResponse } from '@cabukaka/types'
import { formatMongoDoc, mapMongoDocs } from "@cabukaka/utils";
import { getConfig } from "@cabukaka/config";

export function dataService<T>(endpoint: string) {
  const { apiUrl } = getConfig()
  
  const api: AxiosInstance = axios.create({
    baseURL: apiUrl,
    headers: { "Content-Type": "application/json" },
  });

  const handleError = (error: any): never => {
    throw error;
  };

  return {
    getAll: async (filters?: QueryFilters, token?: string): Promise<PaginatedResponse<T>> => {
      try {
        const res = await api.get<PaginatedResponse<T>>(`/${endpoint}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          params: filters,
        });
        return {
          data: mapMongoDocs(res.data.data),
          total: res.data.total,
          pages: res.data.pages
        };
      } catch (error) {
        handleError(error);
      }
      return { data: [], total: 0, pages: 0 };
    },

    getById: async (id: string, token?: string): Promise<T> => {
      try {
        const res = await api.get<T>(`/${endpoint}/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        return formatMongoDoc(res.data);
      } catch (error) {
        handleError(error);
      }
      return {} as T;
    },

    create: async (payload: Partial<T>, token?: string): Promise<T> => {
      try {
        const res = await api.post<T>(`/${endpoint}`, payload, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        return formatMongoDoc(res.data);
      } catch (error) {
        handleError(error);
      }
      return {} as T;
    },

    update: async (id: string, payload: Partial<T>, token?: string): Promise<T> => {
      try {
        const res = await api.put<T>(`/${endpoint}/${id}`, payload, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        return formatMongoDoc(res.data);
      } catch (error) {
        handleError(error);
      }
      return {} as T;
    },

    delete: async (id: string, token?: string): Promise<void> => {
      try {
        await api.delete(`/${endpoint}/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      } catch (error) {
        handleError(error);
      }
    },

    restore: async (id: string, token?: string): Promise<T> => {
      try {
        const res = await api.patch(`/${endpoint}/${id}/restore`, {}, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        return formatMongoDoc(res.data);
      } catch (error) {
        handleError(error);
      }
      return {} as T;
    },

    deleteMany: async (ids: string[], token?: string): Promise<void> => {
      try {
        const promises = ids.map(id => api.delete(`/${endpoint}/${id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }));
        await Promise.all(promises);
      } catch (error) {
        handleError(error);
      }
    },
  };
}